// SSO handshake landing page.
//
// Step in the WordPress → Forge Ultra bridge:
//
//   ki-wealth.com (WP plugin)
//     ──302──►  forge-ultra.ki-wealth.com/login-bridge?token=<JWT>
//                         │
//                         ▼  (this component)
//                 POST /api/v1/auth/login-bridge   { token }
//                         │
//                         ▼  backend verifies sig/exp/iss + roles,
//                            sets the `forge_session` httpOnly cookie
//                         │
//                         ▼  navigate('/dashboard')
//
// We deliberately let the BACKEND be the sole decoder of the JWT — the
// SPA never inspects its claims. That keeps signature/iss validation in
// one place (Python, with the shared secret) and means JS in the
// browser cannot accidentally trust an unverified token.
//
// The token is also mirrored into localStorage as a defence-in-depth
// fallback for browsers that block cross-site cookies (Safari ITP,
// strict 3rd-party cookie blocking). The axios client falls back to
// the Authorization header when the cookie is unavailable.
//
// Failure modes:
//   * No `?token=` query param           → bounce to WP bridge URL.
//   * Backend returns 401 (bad token)    → bounce to WP bridge URL.
//   * Backend returns 403 (wrong level)  → bounce to PMPro membership page.
//   * Backend unreachable / 5xx          → render an inline retry CTA.
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { exchangeTokenForSession } from '../api/auth';
import {
    bounceToLoginOnce,
    clearLoginBounce,
    clearToken,
    setToken,
} from '../api/auth-storage';

const WP_LOGIN_URL: string =
    import.meta.env.VITE_WP_LOGIN_URL ??
    'https://www.ki-wealth.com/sso-bridge';
const WP_MEMBERSHIP_URL: string =
    import.meta.env.VITE_WP_MEMBERSHIP_URL ??
    'https://www.ki-wealth.com/membership-levels/';

type BridgeStatus = 'connecting' | 'failed';

export const LoginBridge = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<BridgeStatus>('connecting');

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            // No token in the URL means the WP redirect was malformed or
            // the visitor reached this page directly. Send them back
            // through the bridge so the WP plugin can re-mint a JWT —
            // BUT route through ``bounceToLoginOnce`` so a WordPress
            // session that does not seed a JWT (e.g. logged-out visitor
            // hitting this URL by accident) cannot trigger an infinite
            // ki-wealth.com ↔ /login-bridge ping-pong.
            // eslint-disable-next-line no-console
            console.warn(
                '[LoginBridge] No ?token= in URL; bouncing to WP bridge.',
            );
            const bounced = bounceToLoginOnce(WP_LOGIN_URL);
            if (!bounced) {
                // Loop guard tripped — show the inline retry CTA instead
                // of bouncing yet again.
                setStatus('failed');
            }
            return;
        }

        let cancelled = false;

        // Mirror the WP-issued token into localStorage BEFORE the network
        // call so the apiClient can attach SOMETHING on the bridge POST
        // itself (even though /auth/login-bridge does not require it).
        // The backend will overwrite our localStorage with the Forge
        // Ultra access token in the response handler below.
        setToken(token);

        // eslint-disable-next-line no-console
        console.info('[LoginBridge] exchanging WP JWT for Forge Ultra session…');

        exchangeTokenForSession(token)
            .then((data) => {
                if (cancelled) return;
                // Replace the WP JWT with the Forge Ultra access JWT
                // returned by /auth/login-bridge. From this point on
                // every API call carries OUR token, not the WP one —
                // which decouples the SPA session lifetime from the
                // (often short) WP token lifetime and enables the
                // silent /auth/refresh flow in api/client.ts.
                if (data.access_token) {
                    setToken(data.access_token);
                }
                // The SSO handshake succeeded — clear any stale
                // bounce-loop marker BEFORE navigating so a transient
                // /users/me hiccup on the dashboard does not get
                // mis-classified as "loop" and lock the user out.
                clearLoginBounce();
                // eslint-disable-next-line no-console
                console.info(
                    '[LoginBridge] session established for user',
                    data.user?.email,
                );
                navigate('/dashboard', { replace: true });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const httpStatus =
                    err instanceof AxiosError ? err.response?.status : undefined;
                // eslint-disable-next-line no-console
                console.error(
                    '[LoginBridge] /auth/login-bridge failed',
                    { status: httpStatus, error: err },
                );
                if (httpStatus === 403) {
                    // Logged in on WP but no allowed PMPro level.
                    clearToken();
                    window.location.href = WP_MEMBERSHIP_URL;
                } else if (httpStatus === 401) {
                    // Token rejected — go back through the bridge so WP
                    // can mint a fresh one. Use bounceToLoginOnce so a
                    // WP that keeps minting bad tokens cannot loop.
                    clearToken();
                    if (!bounceToLoginOnce(WP_LOGIN_URL)) {
                        setStatus('failed');
                    }
                } else {
                    // Backend down / network error — render an inline
                    // retry instead of a redirect loop.
                    setStatus('failed');
                }
            });

        return () => {
            cancelled = true;
        };
    }, [searchParams, navigate]);

    if (status === 'failed') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-200 dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-6 text-center">
                <h1 className="text-2xl font-semibold">
                    Couldn&apos;t establish your Forge Ultra session
                </h1>
                <p className="max-w-md text-base">
                    The Forge Ultra service is temporarily unavailable. Please
                    try again in a moment.
                </p>
                <a
                    href={WP_LOGIN_URL}
                    className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium shadow"
                >
                    Retry sign-in
                </a>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-200 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
            <p className="text-lg font-medium">Connecting to Forge Ultra Dashboard…</p>
        </div>
    );
};

export default LoginBridge;